import "./TvaByQuarter.scss";

import { useEffect, useState } from "react";

import Card from "../../../../components/Card/Card";
import Spinner from "../../../../components/Utils/Spinner/Spinner";
import useLoadingStore from "../../../../stores/LoadingStore";
import useUserStore from "../../../../stores/UserStore";
import {
  QuarterKey,
  quarterOptions,
  QuarterTotals,
} from "../../../../types/Quarter";
import { formatCurrency } from "../../../../Utils/formatCurrency";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

interface TvaByQuarterResponse {
  year: number;
  quarters: QuarterTotals;
  collected: QuarterTotals;
  deductible: QuarterTotals;
  net: QuarterTotals;
}

const currentQuarter = (): number => {
  return Math.ceil((new Date().getMonth() + 1) / 3);
};

const formatAmount = (value: number): string => {
  return value !== 0 ? `${formatCurrency(value)} EUR` : "-";
};

const getNetClassName = (value: number): string => {
  if (value > 0) {
    return "tvaByQuarterMetricValuePositive";
  }

  if (value < 0) {
    return "tvaByQuarterMetricValueNegative";
  }

  return "";
};

const TvaByQuarter = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [tvaData, setTvaData] = useState<TvaByQuarterResponse | null>(null);
  const { loadingStates, setLoading } = useLoadingStore();
  const { isVatSubject } = useUserStore();

  useEffect(() => {
    const fetchData = async () => {
      setLoading("tvaByQuarter", true);
      const data = await appFetch("/api/accounting/dashboard/tvaByQuarter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year }),
      });
      if (data) {
        setTvaData(data);
      }
      setLoading("tvaByQuarter", false);
    };

    fetchData();
  }, [year, setLoading]);

  const isCurrentYear = year === new Date().getFullYear();
  const activeQuarter = isCurrentYear ? currentQuarter() : null;

  if (!isVatSubject) {
    return null;
  }

  return (
    <Card className="tvaByQuarter">
      <div className="tvaByQuarterHeader">
        <div className="tvaByQuarterHeading">
          <h3 className="tvaByQuarterTitle">TVA par trimestre</h3>
          <p className="tvaByQuarterSubtitle">Collectee, deductible et nette</p>
        </div>
        <div className="tvaByQuarterYearNav">
          <button
            className="tvaByQuarterYearBtn"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Annee precedente"
          >
            {"<"}
          </button>
          <span className="tvaByQuarterYear">{year}</span>
          <button
            className="tvaByQuarterYearBtn"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Annee suivante"
          >
            {">"}
          </button>
        </div>
      </div>

      {loadingStates.tvaByQuarter ? (
        <Spinner />
      ) : (
        <div className="tvaByQuarterGrid">
          {quarterOptions.map(({ key, label, period }, idx) => {
            const quarterNum = idx + 1;
            const collected = tvaData?.collected?.[key as QuarterKey] ?? 0;
            const deductible = tvaData?.deductible?.[key as QuarterKey] ?? 0;
            const net = tvaData?.net?.[key as QuarterKey] ?? 0;
            const isActive = activeQuarter === quarterNum;
            const isPast =
              activeQuarter !== null
                ? quarterNum < activeQuarter
                : year < new Date().getFullYear();
            const quarterClassName = [
              "tvaByQuarterQuarter",
              isActive && "tvaByQuarterQuarterActive",
              isPast && "tvaByQuarterQuarterPast",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div key={key} className={quarterClassName}>
                <span className="tvaByQuarterQuarterLabel">{label}</span>
                <span className="tvaByQuarterQuarterPeriod">{period}</span>

                <div className="tvaByQuarterMetrics">
                  <div className="tvaByQuarterMetric">
                    <span className="tvaByQuarterMetricLabel">Collectee</span>
                    <span className="tvaByQuarterMetricValue">
                      {formatAmount(collected)}
                    </span>
                  </div>
                  <div className="tvaByQuarterMetric">
                    <span className="tvaByQuarterMetricLabel">Deductible</span>
                    <span className="tvaByQuarterMetricValue">
                      {formatAmount(deductible)}
                    </span>
                  </div>
                  <div className="tvaByQuarterMetric tvaByQuarterMetricNet">
                    <span className="tvaByQuarterMetricLabel">Nette</span>
                    <span
                      className={`tvaByQuarterMetricValue ${getNetClassName(net)}`}
                    >
                      {formatAmount(net)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default TvaByQuarter;

